import type { Matrix4 } from 'three';
import {
  ACTING,
  actedPose,
  greets,
  ROLES,
  standingClip,
  type Act,
  type ActedPose,
} from '../citizens/citizenActing';
import type { CitizenAsset } from '../citizens/citizenAsset';
import type { CitizenBatch } from '../citizens/citizenBatch';
import type { Casting } from '../citizens/citizenCasting';
import { COMPANY, type CrowdFrame } from './crowdSim';
import type { Residents } from './residents';

/* Standing figures begin their clips and their turns up to this long apart, so neighbours do not
   move as one. */
const APART_S = 12;
const SPREAD = 2654435761;
const lot = (n: number): number => (Math.imul(n + 1, SPREAD) >>> 0) / 2 ** 32;
/* A resident's draw for one of its turns. */
const TURNS = 7919;

/**
 * The crowd's figures as the citizens made for the city (docs/CITIZENS.md): each resident is one
 * figure of its trade, walking its own walk at the pace of its feet, and standing at its business
 * where it has come to. What it does there follows from its seat and its company alone, so it is
 * the same from any distance.
 */
export interface CitizenTier {
  /** Draws entry `i` of a frame where `base` puts it, in the form asked for: 0 is the whole
   *  figure, each next one thinner. */
  add(out: CrowdFrame, i: number, base: Matrix4, form: number, seconds: number): boolean;
}

/** The batch's shapes are the asset's figures in their order, each in its `forms` forms. */
export function createCitizenTier(
  batch: CitizenBatch,
  asset: CitizenAsset,
  casting: Casting,
  residents: Residents,
  forms: number
): CitizenTier {
  const clip = (name: string) => {
    const found = asset.clips[name];
    if (!found) throw new Error(`citizens: no clip ${name}`);
    return found;
  };
  const walks = asset.figures.map((figure) => clip(figure.clips.walk));
  const greeting = clip('Greet');
  const pose: ActedPose = { from: 0, to: 0, mix: 0 };
  const act: Act = {
    moving: false,
    walked: 0,
    scale: 1,
    walk: greeting,
    turned: 0,
    now: greeting,
    before: greeting,
    into: 0,
    clock: 0,
    greet: null,
  };
  return {
    add(out, i, base, form, seconds) {
      const r = out.resident[i]!;
      const cast = casting.figureOf(residents.trade[r]!, r);
      const figure = asset.figures[cast]!;
      const role = ROLES[out.role[i]!]!;
      const keepers = Math.floor(out.met[i]! / COMPANY.keeper),
        visitors = out.met[i]! % COMPANY.keeper;
      const clock = seconds + lot(r) * APART_S;
      const turn = Math.floor(clock / ACTING.turnS);
      act.moving = out.moving[i] === 1;
      act.walked = out.walked[i]!;
      act.scale = residents.scale[r]!;
      act.walk = walks[cast]!;
      act.turned = out.turned[i]!;
      act.clock = clock;
      act.into = clock - turn * ACTING.turnS;
      act.now = clip(standingClip(figure, role, keepers, visitors, lot(r * TURNS + turn)));
      act.before = clip(standingClip(figure, role, keepers, visitors, lot(r * TURNS + turn - 1)));
      act.greet = greets(figure, role, keepers, visitors) ? greeting : null;
      actedPose(act, pose);
      return batch.draw(cast * forms + form, base, pose.from, pose.to, pose.mix);
    },
  };
}
